import {
  AnyWizardDefinition,
  WizardSession,
  WizardStepDefinition,
} from "./types";

/** Whether discarding or restarting the session would lose anything. */
export function sessionHasProgress(
  definition: AnyWizardDefinition,
  session: WizardSession,
): boolean {
  return (
    session.completedStepIds.length > 0 ||
    session.currentStepId !== definition.steps[0]?.id ||
    session.updatedAt !== session.startedAt
  );
}

export function getStepProgress<Data, Context>(
  applicableSteps: WizardStepDefinition<Data, Context>[],
  session: WizardSession,
): { done: number; total: number } {
  const isCompleted = session.completedAt !== null;
  return {
    done: applicableSteps.filter(
      (step) => isCompleted || session.completedStepIds.includes(step.id),
    ).length,
    total: applicableSteps.length,
  };
}

export function getApplicableSteps<Data, Context>(
  steps: WizardStepDefinition<Data, Context>[],
  data: Data,
  context: Context,
): WizardStepDefinition<Data, Context>[] {
  return steps.filter(
    (step) => !step.isApplicable || step.isApplicable(data, context),
  );
}

/**
 * The steps the panel last recorded as applicable to the session. Every step
 * counts until the panel has rendered it.
 */
export function getRecordedApplicableSteps<Data, Context>(
  steps: WizardStepDefinition<Data, Context>[],
  session: WizardSession,
): WizardStepDefinition<Data, Context>[] {
  const recorded = session.applicableStepIds;
  const applicable = recorded
    ? steps.filter((step) => recorded.includes(step.id))
    : [];
  return applicable.length > 0 ? applicable : steps;
}

/**
 * Resolves the step to render. The stored step can stop applying after the
 * session was saved (e.g. the account left HIPAA mode), in which case we fall
 * forward to the next applicable step in definition order.
 */
export function resolveCurrentStepId<Data, Context>(
  allSteps: WizardStepDefinition<Data, Context>[],
  applicableSteps: WizardStepDefinition<Data, Context>[],
  storedStepId: string,
): string | undefined {
  if (applicableSteps.some((step) => step.id === storedStepId)) {
    return storedStepId;
  }

  const storedIndex = allSteps.findIndex((step) => step.id === storedStepId);
  const applicableIds = new Set(applicableSteps.map((step) => step.id));
  const nextApplicable = allSteps
    .slice(storedIndex + 1)
    .find((step) => applicableIds.has(step.id));

  return nextApplicable?.id ?? applicableSteps[applicableSteps.length - 1]?.id;
}

export function getAdjacentStepId<Data, Context>(
  applicableSteps: WizardStepDefinition<Data, Context>[],
  currentStepId: string,
  direction: 1 | -1,
): string | undefined {
  const index = applicableSteps.findIndex((step) => step.id === currentStepId);
  if (index === -1) {
    return undefined;
  }
  return applicableSteps[index + direction]?.id;
}
